import {keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient} from '@tanstack/react-query'
import {PipelineRepository} from '../repositories/PipelineRepository.ts'
import {AuthenticationRepository} from '../repositories/AuthenticationRepository.ts'
import {ActivationChannel} from '../domain/entities/Activation.ts'
import {BoardFilters, CrmCommand, CrmStage, NoteFile, NotesFilter, PipelineId, TasksPage, TasksQuery} from '../domain/entities/Pipeline.ts'
import {ConversionBoardFilters, ConversionCommand, ConversionStage} from '../domain/entities/ConversionPipeline.ts'
import {ACTIVATION_QUERY_KEY, ACTIVATIONS_QUERY_KEY} from './useActivations.ts'

const pipelineRepository = new PipelineRepository(new AuthenticationRepository())

export const CRM_BOARD_QUERY_KEY = 'crm-board' as const
export const CRM_ACTIVATION_QUERY_KEY = 'crm-activation' as const
export const CRM_CONVERSION_BOARD_QUERY_KEY = 'crm-conversion-board' as const
export const CRM_CONVERSION_QUERY_KEY = 'crm-conversion' as const
const CRM_HISTORY_QUERY_KEY = 'crm-history' as const
const CRM_NOTES_QUERY_KEY = 'crm-notes' as const
const CRM_TASKS_QUERY_KEY = 'crm-tasks' as const
const CRM_TEAM_QUERY_KEY = 'crm-team' as const

// The board has no Firestore listener (contract §6): poll while it is open.
const BOARD_POLL_MS = 60_000
const COLUMN_PAGE_SIZE = 50
const HISTORY_PAGE_SIZE = 20
const TASKS_PAGE_SIZE = 50

// GET /crm/pipelines/activation/board — every column with its first 50 cards.
export function useActivationBoard(filters: BoardFilters) {
    const query = useQuery({
        queryKey: [CRM_BOARD_QUERY_KEY, filters],
        queryFn: () => pipelineRepository.getActivationBoard({...filters, limit: COLUMN_PAGE_SIZE}),
        placeholderData: keepPreviousData,
        refetchInterval: BOARD_POLL_MS,
    })
    return {
        board: query.data ?? null,
        isLoadingBoard: query.isLoading,
        isFetchingBoard: query.isFetching,
        boardError: query.error,
        refetchBoard: query.refetch,
    }
}

// GET /crm/pipelines/conversion/board — 24 columns with their first 50 cards.
export function useConversionBoard(filters: ConversionBoardFilters) {
    const query = useQuery({
        queryKey: [CRM_CONVERSION_BOARD_QUERY_KEY, filters],
        queryFn: () => pipelineRepository.getConversionBoard({...filters, limit: COLUMN_PAGE_SIZE}),
        placeholderData: keepPreviousData,
        refetchInterval: BOARD_POLL_MS,
    })
    return {
        board: query.data ?? null,
        isLoadingBoard: query.isLoading,
        isFetchingBoard: query.isFetching,
        boardError: query.error,
        refetchBoard: query.refetch,
    }
}

export type ColumnSource =
    | {pipeline: 'activation'; stage: CrmStage; filters: BoardFilters}
    | {pipeline: 'conversion'; stage: ConversionStage; filters: ConversionBoardFilters}

// "Load more" on one column of either board: ?stage=&cursor= pages after the
// board's first page (`startCursor` = that column's nextCursor on the board).
export function useBoardColumnMore<TCard>(source: ColumnSource, startCursor: string | null, enabled: boolean) {
    const fetchPage = (cursor: string | undefined): Promise<{cards: TCard[]; nextCursor: string | null}> => {
        const page: Promise<{columns?: {cards?: unknown[]; nextCursor?: string | null}[]}> = source.pipeline === 'conversion'
            ? pipelineRepository.getConversionBoard({...source.filters, stage: source.stage, cursor, limit: COLUMN_PAGE_SIZE})
            : pipelineRepository.getActivationBoard({...source.filters, stage: source.stage, cursor, limit: COLUMN_PAGE_SIZE})
        return page.then((board) => ({
            cards: (board?.columns?.[0]?.cards ?? []) as TCard[],
            nextCursor: board?.columns?.[0]?.nextCursor ?? null,
        }))
    }
    const boardKey = source.pipeline === 'conversion' ? CRM_CONVERSION_BOARD_QUERY_KEY : CRM_BOARD_QUERY_KEY
    const query = useInfiniteQuery({
        queryKey: [boardKey, source.filters, 'column', source.stage, startCursor],
        queryFn: ({pageParam}) => fetchPage(pageParam),
        initialPageParam: startCursor ?? undefined,
        getNextPageParam: (last) => last?.nextCursor ?? undefined,
        enabled: enabled && !!startCursor,
    })
    return {
        moreCards: query.data?.pages.flatMap((page) => page?.cards ?? []) ?? [],
        hasMore: query.data ? !!query.hasNextPage : !!startCursor,
        isLoadingMore: query.isFetching,
        loadMore: query.fetchNextPage,
    }
}

// GET /crm/activations/{id}; `channel` forces another prepared-message channel.
export function useCrmActivation(id: string | null, channel?: ActivationChannel) {
    const query = useQuery({
        queryKey: [CRM_ACTIVATION_QUERY_KEY, id, channel ?? null],
        queryFn: () => pipelineRepository.getActivation(id!, channel),
        enabled: !!id,
        placeholderData: keepPreviousData,
        retry: 1,
    })
    return {
        detail: query.data ?? null,
        isLoadingDetail: query.isLoading,
        isFetchingDetail: query.isFetching,
        detailError: query.error,
    }
}

// GET /crm/conversions/{id} (§3.2).
export function useCrmConversion(id: string | null) {
    const query = useQuery({
        queryKey: [CRM_CONVERSION_QUERY_KEY, id],
        queryFn: () => pipelineRepository.getConversion(id!),
        enabled: !!id,
        placeholderData: keepPreviousData,
        retry: 1,
    })
    return {
        detail: query.data ?? null,
        isLoadingDetail: query.isLoading,
        isFetchingDetail: query.isFetching,
        detailError: query.error,
    }
}

// GET /crm/{activations|conversions}/{id}/history — the full list behind "See all".
export function useCrmHistory(pipeline: PipelineId, id: string | null, enabled: boolean) {
    const query = useInfiniteQuery({
        queryKey: [CRM_HISTORY_QUERY_KEY, pipeline, id],
        queryFn: ({pageParam}) => pipelineRepository.getHistory(pipeline, id!, HISTORY_PAGE_SIZE, pageParam),
        initialPageParam: undefined as string | undefined,
        getNextPageParam: (last) => last?.nextCursor ?? undefined,
        enabled: !!id && enabled,
    })
    return {
        events: query.data?.pages.flatMap((page) => page?.items ?? []) ?? [],
        isLoadingHistory: query.isLoading,
        historyError: query.error,
        hasMoreHistory: !!query.hasNextPage,
        isFetchingMoreHistory: query.isFetchingNextPage,
        loadMoreHistory: query.fetchNextPage,
    }
}

export function useCrmNotes(pipeline: PipelineId, id: string | null, filter: NotesFilter) {
    const query = useQuery({
        queryKey: [CRM_NOTES_QUERY_KEY, pipeline, id, filter],
        queryFn: () => pipelineRepository.getNotes(pipeline, id!, filter),
        enabled: !!id,
        placeholderData: keepPreviousData,
    })
    return {
        notes: query.data?.items ?? [],
        isLoadingNotes: query.isLoading,
        notesError: query.error,
    }
}

// The backend filters each Firestore page, so a page can come back short or
// empty with a cursor (§9.6): keep reading until a page is full or the end.
const MAX_TASK_PAGE_HOPS = 5
async function fetchTasksPage(query: TasksQuery, cursor: string | undefined): Promise<TasksPage> {
    const items: TasksPage['items'] = []
    let next: string | null | undefined = cursor
    for (let hop = 0; hop < MAX_TASK_PAGE_HOPS; hop++) {
        const page = await pipelineRepository.getTasks(query, TASKS_PAGE_SIZE, next ?? undefined)
        items.push(...(page?.items ?? []))
        next = page?.nextCursor
        if (!next || items.length >= TASKS_PAGE_SIZE) break
    }
    return {items, nextCursor: next ?? null}
}

// GET /crm/tasks — `assignee` is a uid, 'me', 'unassigned' or 'all'.
export function useCrmTasks(query: TasksQuery) {
    const result = useInfiniteQuery({
        queryKey: [CRM_TASKS_QUERY_KEY, query],
        queryFn: ({pageParam}) => fetchTasksPage(query, pageParam),
        initialPageParam: undefined as string | undefined,
        getNextPageParam: (last) => last?.nextCursor ?? undefined,
        placeholderData: keepPreviousData,
        refetchInterval: BOARD_POLL_MS,
    })
    return {
        tasks: result.data?.pages.flatMap((page) => page?.items ?? []) ?? [],
        isLoadingTasks: result.isLoading,
        isFetchingTasks: result.isFetching,
        tasksError: result.error,
        hasMoreTasks: !!result.hasNextPage,
        isFetchingMoreTasks: result.isFetchingNextPage,
        loadMoreTasks: result.fetchNextPage,
        refetchTasks: result.refetch,
    }
}

// GET /crm/team — maintained by hand in Firestore; cache for the session.
export function useCrmTeam() {
    const query = useQuery({
        queryKey: [CRM_TEAM_QUERY_KEY],
        queryFn: () => pipelineRepository.getTeam(),
        staleTime: Infinity,
        retry: 1,
    })
    return {
        team: query.data?.items ?? [],
        isLoadingTeam: query.isLoading,
        teamError: query.error,
    }
}

export function useCrmMutations() {
    const queryClient = useQueryClient()
    // A command can move the card, change its next touch, add history and
    // tasks, and (UPDATE_CONTACT, MARK_LOST) change the phase 1-2 views too.
    const invalidate = () => {
        for (const key of [CRM_BOARD_QUERY_KEY, CRM_ACTIVATION_QUERY_KEY, CRM_HISTORY_QUERY_KEY, CRM_NOTES_QUERY_KEY,
            CRM_TASKS_QUERY_KEY, ACTIVATIONS_QUERY_KEY, ACTIVATION_QUERY_KEY, CRM_CONVERSION_BOARD_QUERY_KEY]) {
            queryClient.invalidateQueries({queryKey: [key]})
        }
    }

    const commandMutation = useMutation({
        mutationFn: ({id, command}: {id: string; command: CrmCommand}) => pipelineRepository.sendCommand(id, command),
        onSettled: invalidate,
    })

    // A Conversion command answers with the fresh detail (§4): seed the panel
    // with it so it does not wait for the refetch.
    const conversionCommandMutation = useMutation({
        mutationFn: ({id, command}: {id: string; command: ConversionCommand}) => pipelineRepository.sendConversionCommand(id, command),
        onSuccess: (detail, {id}) => { if (detail) queryClient.setQueryData([CRM_CONVERSION_QUERY_KEY, id], detail) },
        onSettled: () => {
            for (const key of [CRM_CONVERSION_BOARD_QUERY_KEY, CRM_CONVERSION_QUERY_KEY, CRM_HISTORY_QUERY_KEY, CRM_NOTES_QUERY_KEY, CRM_TASKS_QUERY_KEY]) {
                queryClient.invalidateQueries({queryKey: [key]})
            }
        },
    })

    const detailKey = (pipeline: PipelineId) => (pipeline === 'conversion' ? CRM_CONVERSION_QUERY_KEY : CRM_ACTIVATION_QUERY_KEY)

    const addNoteMutation = useMutation({
        mutationFn: ({pipeline, id, text, files}: {pipeline: PipelineId; id: string; text: string; files?: NoteFile[]}) =>
            pipelineRepository.addNote(pipeline, id, text, files),
        onSuccess: (_, {pipeline, id}) => {
            queryClient.invalidateQueries({queryKey: [CRM_NOTES_QUERY_KEY, pipeline, id]})
            queryClient.invalidateQueries({queryKey: [CRM_HISTORY_QUERY_KEY, pipeline, id]})
            queryClient.invalidateQueries({queryKey: [detailKey(pipeline), id]})
        },
    })

    const pinNoteMutation = useMutation({
        mutationFn: ({pipeline, id, noteId, pinned}: {pipeline: PipelineId; id: string; noteId: string; pinned: boolean}) =>
            pipelineRepository.setNotePinned(pipeline, id, noteId, pinned),
        onSuccess: (_, {pipeline, id}) => queryClient.invalidateQueries({queryKey: [CRM_NOTES_QUERY_KEY, pipeline, id]}),
    })

    return {
        sendCrmCommand: commandMutation.mutateAsync,
        isSendingCrmCommand: commandMutation.isPending,
        sendConversionCommand: conversionCommandMutation.mutateAsync,
        addCrmNote: addNoteMutation.mutateAsync,
        isAddingCrmNote: addNoteMutation.isPending,
        setCrmNotePinned: pinNoteMutation.mutateAsync,
    }
}
